import React, { useEffect, useState, useCallback } from 'react';
import { todayIso, fmtDateDMY, fmtTimeHHMM, syncDateRange } from '../utils/dateUtils';
import InvoiceAmendModal from '../components/InvoiceAmendModal';
import InvoiceReissueModal from '../components/InvoiceReissueModal';

const toMoney = (v) => Number(v || 0).toFixed(2);

// A CORPORATE/TRAVEL_AGENT checkout posts a CHECKOUT_CITY_LEDGER_TRANSFER (or
// FOLIO_ROUTE_TRANSFER) credit that closes the GUEST's folio by moving the
// balance to that party's own account receivable -- it is not money anyone
// has paid. Shared between the printed invoice (buildInvoiceHtml) and the
// Invoice Detail modal so neither ever counts it as a real payment when
// computing what's still due.
const BILLED_TRANSFER_TYPES = new Set(['CHECKOUT_CITY_LEDGER_TRANSFER', 'FOLIO_ROUTE_TRANSFER']);

// REQ-18 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md) — defensive fallback only, for
// the unlikely case hotel.invoice_terms_text is missing from the API
// response. The real default lives server-side (pms_property_config column
// default) so every property already has this exact text without needing
// this constant at all in the normal case.
const DEFAULT_INVOICE_TERMS_TEXT_FALLBACK = `1. This is a computer-generated invoice.
2. I agree that I am responsible for the full payment of this bill in the event it is not paid by the Company, Organisation or Person indicated.
3. Subject to local jurisdiction. E. & O.E.`;

// ── Indian Number-to-Words ─────────────────────────────────────────────────
const numberToWords = (amount) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convert = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  const num = Math.abs(Number(amount) || 0);
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let words = rupees === 0 ? 'Zero' : convert(rupees);
  words = 'Rupees ' + words;
  if (paise > 0) words += ' and ' + convert(paise) + ' Paise';
  return words + ' Only';
};

// ── SAC code lookup (GST Services Classification) ──────────────────────────
const SAC_CODES = {
  ROOM_TARIFF: '996311',
  KOT_ROOM_CHARGE: '996331',
  BAR_ROOM_CHARGE: '996331',
  ROOM_SERVICE: '996331',
  LAUNDRY: '999710',
  MINIBAR_ROOM_CHARGE: '996331',
  LATE_CHECKOUT: '996311',
  ALLOWANCE: '996311',
  MANUAL: '999790',
  BANQUET_CHARGE: '996339'
};

// ── Friendly description for posting types ──────────────────────────────────
const POSTING_LABELS = {
  ROOM_TARIFF: 'Room Charges',
  KOT_ROOM_CHARGE: 'Food & Beverage',
  BAR_ROOM_CHARGE: 'Bar Charges',
  ROOM_SERVICE: 'Room Service',
  LAUNDRY: 'Laundry Service',
  MINIBAR_ROOM_CHARGE: 'Minibar',
  LATE_CHECKOUT: 'Late Checkout',
  ALLOWANCE: 'Allowance / Waiver',
  MANUAL: 'Miscellaneous',
  BANQUET_CHARGE: 'Banquet Charges'
};

// ── Professional Tax Invoice HTML Builder ──────────────────────────────────
const buildInvoiceHtml = (inv, printLines, splitLabel, copyLabel) => {
  if (!inv) return '';
  const lines = Array.isArray(printLines) ? printLines : [];
  const hotel = inv.hotel || {};
  const guest = inv.guest_detail || {};
  const corp = inv.corporate_detail || null;

  // Compute totals
  const totalDebit = lines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
  const totalSgst = lines.reduce((s, l) => s + Number(l.sgst_amount || 0), 0);
  const totalCgst = lines.reduce((s, l) => s + Number(l.cgst_amount || 0), 0);
  const totalIgst = lines.reduce((s, l) => s + Number(l.igst_amount || 0), 0);
  const totalTaxableValue = lines.reduce((s, l) => s + (Number(l.debit_amount || 0) - Number(l.tax_amount || 0)), 0);
  // Net Payable must not treat a billed-to-party transfer as a payment, or a
  // printed corporate-billed tax invoice reads "Net Payable: 0.00" as if the
  // corporate had already settled it -- confirmed live (INV-003611, V Ships
  // India Pvt. Ltd., ₹4,833 transferred to their account, nothing paid yet).
  // See BILLED_TRANSFER_TYPES at module scope, near the top of this file.
  const totalBilledTransfer = lines
    .filter(l => Number(l.credit_amount || 0) > 0 && BILLED_TRANSFER_TYPES.has(String(l.posting_type || '').toUpperCase()))
    .reduce((s, l) => s + Number(l.credit_amount || 0), 0);
  const totalRealPayments = totalCredit - totalBilledTransfer;
  const netPayable = totalDebit - totalRealPayments;
  const roundOff = Math.round(netPayable) - netPayable;
  const payableAmount = Math.round(netPayable);

  // Calculate room nights
  const ci = inv.checkin_datetime ? new Date(inv.checkin_datetime) : null;
  const co = inv.checkout_datetime ? new Date(inv.checkout_datetime) : null;
  const roomNts = ci && co ? Math.max(1, Math.ceil((co - ci) / 86400000)) : '-';

  const fmtDate = (dt) => fmtDateDMY(dt);
  const fmtTime = (dt) => fmtTimeHHMM(dt);

  const hotelName = hotel.billing_company_name || hotel.property_name || 'Hotel';
  const hotelAddr = [hotel.address_line1, hotel.address_line2, hotel.city, hotel.state, hotel.pin_code, hotel.country]
    .filter(Boolean).join(', ');
  const hotelPhone = hotel.phone || '';
  const hotelEmail = hotel.email || '';
  const hotelWebsite = hotel.website || '';
  const hotelGstin = hotel.gstin_number || '';
  const hotelState = hotel.state || '';
  const hotelStateCode = hotelGstin ? hotelGstin.substring(0, 2) : '';

  // Guest details — prefer the invoice-level fields (which the Narrative Edit
  // amendment writes to: pms_invoice_master.customer_gstin / place_of_supply /
  // customer_state_code). Fall back to the guest_master snapshot when the
  // invoice doesn't carry an override.
  const guestGstin = inv.customer_gstin || guest.guest_gstin || '';
  const guestCompany = guest.guest_company || '';
  const guestAddr = [guest.guest_address, guest.guest_city, guest.guest_state, guest.guest_country, guest.guest_pin]
    .filter(Boolean).join(', ') || '';
  const guestEmail = guest.guest_email || inv.guest_email || '';
  const guestPlaceOfSupply = inv.place_of_supply || '';
  const guestStateCode = inv.customer_state_code || (guestGstin ? guestGstin.substring(0, 2) : '');
  const isB2B = !!(guestGstin || guestCompany);

  // Meal plan from rate plan
  const mealPlanMap = { EP: 'European Plan (Room Only)', CP: 'Continental Plan (Breakfast)', MAP: 'Modified American Plan (B+D)', AP: 'American Plan (All Meals)' };
  const mealPlanCode = (guest.meal_plan || 'EP').toUpperCase();
  const mealPlanLabel = mealPlanMap[mealPlanCode] || mealPlanCode;
  const ratePlanCode = guest.rate_plan_code || '';

  // Only charge lines (debits) for the itemized table
  const chargeLines = lines.filter(l => Number(l.debit_amount || 0) > 0);

  // Payment/credit lines — itemized. Split out CHECKOUT_CITY_LEDGER_TRANSFER/
  // FOLIO_ROUTE_TRANSFER (billed to the corporate/agent's own account, not a
  // real payment) so it prints under its own "Billed to" heading instead of
  // being counted as "Total Paid" — see BILLED_TRANSFER_TYPES/netPayable above.
  const paymentLines = lines.filter(l => Number(l.credit_amount || 0) > 0 && !BILLED_TRANSFER_TYPES.has(String(l.posting_type || '').toUpperCase()));
  const totalPayments = paymentLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
  const billedTransferLines = lines.filter(l => Number(l.credit_amount || 0) > 0 && BILLED_TRANSFER_TYPES.has(String(l.posting_type || '').toUpperCase()));
  const billedTransferPartyLabel = corp?.corporate_name
    ? `Billed to ${corp.corporate_name}`
    : (inv.settled_agent_name || inv.agent_name)
      ? `Billed to ${inv.settled_agent_name || inv.agent_name}`
      : (String(inv.settlement_mode || '').toUpperCase() === 'TRAVEL_AGENT' ? 'Billed to Travel Agent' : 'Billed to Corporate');

  // Build tax summary grouped by tax rate
  const taxGroups = {};
  chargeLines.forEach(l => {
    const rate = Number(l.gst_percent || 0);
    if (rate <= 0) return;
    if (!taxGroups[rate]) taxGroups[rate] = { taxable: 0, cgst: 0, sgst: 0, igst: 0 };
    taxGroups[rate].taxable += Number(l.debit_amount || 0) - Number(l.tax_amount || 0);
    taxGroups[rate].cgst += Number(l.cgst_amount || 0);
    taxGroups[rate].sgst += Number(l.sgst_amount || 0);
    taxGroups[rate].igst += Number(l.igst_amount || 0);
  });
  const taxGroupKeys = Object.keys(taxGroups).sort((a, b) => Number(a) - Number(b));
  const hasIgst = totalIgst > 0;

  const invTitle = splitLabel ? `${inv.invoice_no || 'Invoice'} - ${splitLabel}` : (inv.invoice_no || 'Invoice');
  const invoiceDate = fmtDate(inv.checkout_datetime || inv.created_at);

  // Friendly posting label
  const getDescription = (line) => {
    const pt = String(line.posting_type || '').toUpperCase();
    if (line.remarks && line.remarks.length > 5) return line.remarks;
    return POSTING_LABELS[pt] || line.revenue_desc || line.posting_type || '-';
  };

  // Payment type label
  const getPaymentLabel = (line) => {
    const pt = String(line.posting_type || '').toUpperCase();
    if (pt === 'ADVANCE') return 'Advance Payment';
    if (pt === 'CHECKOUT_PAYMENT') return 'Payment at Checkout';
    if (pt.includes('CORPORATE')) return 'Corporate Credit';
    if (pt.includes('CARD')) return 'Card Payment';
    if (pt.includes('UPI')) return 'UPI Payment';
    return line.remarks || line.revenue_desc || pt || 'Payment';
  };

  // Line items HTML
  const rowsHtml = chargeLines.map((line, idx) => {
    const baseAmt = Number(line.debit_amount || 0) - Number(line.tax_amount || 0);
    const pt = String(line.posting_type || '').toUpperCase();
    const sacCode = line.sac_code || SAC_CODES[pt] || '999790';
    return `<tr style="${idx % 2 === 1 ? 'background:#fafafa;' : ''}">
      <td class="tc">${idx + 1}</td>
      <td class="tl">${fmtDate(line.posting_date || line.created_at)}</td>
      <td class="tl">${getDescription(line)}</td>
      <td class="tc">${sacCode}</td>
      <td class="tc">${line.room_number || inv.room_number || ''}</td>
      <td class="tr">${toMoney(baseAmt)}</td>
      <td class="tc">${line.gst_percent ? Number(line.gst_percent) + '%' : '-'}</td>
      ${hasIgst
        ? `<td class="tr">${toMoney(line.igst_amount || 0)}</td>`
        : `<td class="tr">${toMoney(line.cgst_amount || 0)}</td>
           <td class="tr">${toMoney(line.sgst_amount || 0)}</td>`
      }
      <td class="tr" style="font-weight:600;">${toMoney(line.debit_amount)}</td>
    </tr>`;
  }).join('');

  // Tax summary rows
  const taxSummaryHtml = taxGroupKeys.map(rate => {
    const g = taxGroups[rate];
    return `<tr>
      <td class="tl" style="font-size:10px;">${Number(rate)}%</td>
      <td class="tr" style="font-size:10px;">${toMoney(g.taxable)}</td>
      ${hasIgst
        ? `<td class="tr" style="font-size:10px;">${toMoney(g.igst)}</td>`
        : `<td class="tr" style="font-size:10px;">${toMoney(g.cgst)}</td>
           <td class="tr" style="font-size:10px;">${toMoney(g.sgst)}</td>`
      }
      <td class="tr" style="font-size:10px;font-weight:600;">${toMoney(g.cgst + g.sgst + g.igst)}</td>
    </tr>`;
  }).join('');

  // Payment rows
  const paymentRowsHtml = paymentLines.map(l => {
    return `<tr>
      <td style="padding:2px 8px;font-size:10px;">${getPaymentLabel(l)}${l.payment_mode ? ` <span style="color:#777;font-weight:400;">(${l.payment_mode}${l.reference_no ? ` — ${l.reference_no}` : ''})</span>` : ''}</td>
      <td style="padding:2px 8px;font-size:10px;text-align:right;">${toMoney(l.credit_amount)}</td>
    </tr>`;
  }).join('');

  // Billed-to-corporate/agent rows — informational only, never folded into
  // "Total Paid" (see BILLED_TRANSFER_TYPES above).
  const billedTransferRowsHtml = billedTransferLines.map(l => {
    return `<tr>
      <td style="padding:2px 8px;font-size:10px;">${billedTransferPartyLabel}</td>
      <td style="padding:2px 8px;font-size:10px;text-align:right;">${toMoney(l.credit_amount)}</td>
    </tr>`;
  }).join('');

  const colCount = hasIgst ? 9 : 10;

  // Reorderable Invoice Field Configuration (Corporate Master) — each entry
  // preserves the exact gating condition the inline ternary used to have;
  // vessel_name/guest_gstin/mobile also carry their unconditional trailing
  // sibling (Company/Address/Email) so that visual relationship never changes
  // even when the corporate reorders the 12 real toggle fields around them.
  const corpHasContent = corp && corp.corporate_name;
  const FIELD_RENDERERS = {
    invoice_show_guest_name: () => (!corp || corp.invoice_show_guest_name !== false)
      ? `<tr><td class="lbl">Name</td><td class="val" style="font-weight:600;">${inv.guest_name || '-'}</td></tr>` : '',
    invoice_show_company_name: () => (corpHasContent && corp.invoice_show_company_name !== false)
      ? `<tr><td class="lbl">Corporate</td><td class="val" style="font-weight:600;">${corp.corporate_name}</td></tr>` : '',
    invoice_show_corporate_code: () => (corpHasContent && corp.invoice_show_corporate_code !== false && corp.corporate_code)
      ? `<tr><td class="lbl">Corp. Code</td><td class="val">${corp.corporate_code}</td></tr>` : '',
    invoice_show_care_of: () => (corpHasContent && corp.invoice_show_care_of && corp.care_of)
      ? `<tr><td class="lbl">Corp. C/O</td><td class="val">${corp.care_of}</td></tr>` : '',
    invoice_show_gst: () => (corpHasContent && corp.invoice_show_gst !== false && corp.gst_number)
      ? `<tr><td class="lbl">Corp. GSTIN</td><td class="val" style="font-weight:600;">${corp.gst_number}</td></tr>` : '',
    invoice_show_address: () => (corpHasContent && corp.invoice_show_address !== false && corp.billing_address)
      ? `<tr><td class="lbl">Corp. Address</td><td class="val" style="font-size:10px;">${corp.billing_address}</td></tr>` : '',
    invoice_show_employee_id: () => (corpHasContent && corp.invoice_show_employee_id && guest.employee_id)
      ? `<tr><td class="lbl">Employee ID</td><td class="val">${guest.employee_id}</td></tr>` : '',
    invoice_show_vessel_name: () => ((corpHasContent && corp.invoice_show_vessel_name && guest.vessel_name)
        ? `<tr><td class="lbl">Vessel Name(s)</td><td class="val">${guest.vessel_name}</td></tr>` : '')
      + (guestCompany ? `<tr><td class="lbl">Company</td><td class="val">${guestCompany}</td></tr>` : ''),
    invoice_show_guest_gstin: () => (((!corp || corp.invoice_show_guest_gstin !== false) && guestGstin)
        ? `<tr><td class="lbl">Guest GSTIN</td><td class="val" style="font-weight:600;">${guestGstin}</td></tr>` : '')
      + (guestAddr ? `<tr><td class="lbl">Address</td><td class="val" style="font-size:10px;">${guestAddr}</td></tr>` : ''),
    invoice_show_mobile: () => ((!corp || corp.invoice_show_mobile !== false)
        ? `<tr><td class="lbl">Mobile</td><td class="val">${inv.guest_mobile || '-'}</td></tr>` : '')
      + (guestEmail ? `<tr><td class="lbl">Email</td><td class="val" style="font-size:10px;">${guestEmail}</td></tr>` : ''),
    invoice_show_nationality: () => (!corp || corp.invoice_show_nationality !== false)
      ? `<tr><td class="lbl">Nationality</td><td class="val">${guest.nationality || 'INDIAN'}</td></tr>` : '',
    invoice_show_pax: () => (!corp || corp.invoice_show_pax !== false)
      ? `<tr><td class="lbl">Pax</td><td class="val">${guest.total_pax || '-'}</td></tr>` : '',
    // Guest's own per-booking billing address (pms_guest_master.billing_address,
    // captured on the reservation) -- distinct from invoice_show_address above,
    // which is the CORPORATE's own master-data address. Same "shows on any
    // invoice, opt-OUT via the toggle" pattern as guest_gstin/mobile/
    // nationality/pax just above -- NOT corp-gated like employee_id/
    // vessel_name, since it's a guest-level field that should print on a
    // plain CASH/SELF invoice too (confirmed live: Company Name/Guest GSTIN
    // already do exactly this with no corporate involved at all).
    invoice_show_billing_address: () => ((!corp || corp.invoice_show_billing_address !== false) && guest.guest_billing_address)
      ? `<tr><td class="lbl">Billing Address</td><td class="val" style="font-size:10px;">${guest.guest_billing_address}</td></tr>` : '',
  };
  const FIELD_ORDER_DEFAULT = ['invoice_show_guest_name', 'invoice_show_company_name', 'invoice_show_corporate_code',
    'invoice_show_care_of', 'invoice_show_gst', 'invoice_show_address', 'invoice_show_employee_id',
    'invoice_show_vessel_name', 'invoice_show_guest_gstin', 'invoice_show_mobile', 'invoice_show_nationality', 'invoice_show_pax',
    'invoice_show_billing_address'];
  const fieldOrder = (corp && corp.invoice_field_order ? corp.invoice_field_order.split(',') : FIELD_ORDER_DEFAULT)
    .filter((k) => FIELD_RENDERERS[k]);
  const guestDetailsRowsHtml = fieldOrder.map((k) => FIELD_RENDERERS[k]()).join('');

  return `<html><head><title>${invTitle}</title>
<style>
  @page { size: A4; margin: 10mm 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; padding: 8px; }
  table { border-collapse: collapse; }
  .inv { max-width:800px; margin:0 auto; border:2px solid #333; }
  .hdr-row { display:flex; align-items:center; padding:12px 18px; border-bottom:2px solid #333; }
  .tc { padding:5px 6px; border:1px solid #bbb; text-align:center; font-size:10.5px; }
  .tl { padding:5px 6px; border:1px solid #bbb; text-align:left; font-size:10.5px; }
  .tr { padding:5px 6px; border:1px solid #bbb; text-align:right; font-size:10.5px; }
  .sec-border { border-bottom:1.5px solid #333; }
  .lbl { padding:2px 0; font-weight:600; color:#333; width:110px; font-size:10.5px; }
  .val { padding:2px 0; font-size:10.5px; }
  @media print { body { padding: 0; } .no-print { display:none !important; } }
</style>
</head><body>
<div class="inv">

  <!-- HEADER with Logo + Hotel Info -->
  <div class="hdr-row" style="justify-content:center;flex-direction:column;text-align:center;">
    ${hotel.logo_url ? `<img src="${hotel.logo_url.startsWith('http') ? hotel.logo_url : window.location.origin + hotel.logo_url}" alt="Logo" style="height:50px;margin-bottom:6px;" onerror="this.style.display='none'"><br>` : ''}
    <div style="font-size:17px;font-weight:700;letter-spacing:1.5px;color:#1a1a1a;">${hotelName}</div>
    ${hotelAddr ? `<div style="font-size:10px;margin-top:3px;color:#444;">${hotelAddr}</div>` : ''}
    <div style="font-size:9.5px;color:#555;margin-top:2px;">
      ${[hotelPhone ? `Tel: ${hotelPhone}` : '', hotelEmail ? `Email: ${hotelEmail}` : '', hotelWebsite ? `Web: ${hotelWebsite}` : ''].filter(Boolean).join(' | ')}
    </div>
    ${hotelGstin ? `<div style="font-size:10px;margin-top:3px;font-weight:600;">GSTIN: ${hotelGstin}${hotelStateCode ? ` | State Code: ${hotelStateCode}` : ''}</div>` : ''}
  </div>

  <!-- TAX INVOICE title bar -->
  <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 18px;background:#f0f0f0;border-bottom:2px solid #333;font-weight:700;">
    <div style="font-size:14px;letter-spacing:3px;">TAX INVOICE</div>
    <div style="text-align:right;font-size:10.5px;">
      <div>Invoice No: <span style="font-size:12px;">${inv.invoice_no || '-'}${splitLabel ? ` (${splitLabel})` : ''}</span></div>
      <div style="font-weight:400;margin-top:1px;">Date: ${invoiceDate}</div>
    </div>
  </div>

  ${copyLabel ? `<div style="text-align:right;padding:3px 18px;font-size:9.5px;font-weight:700;font-style:italic;color:#555;border-bottom:1px solid #ddd;">${copyLabel}</div>` : ''}

  <!-- GUEST + STAY INFO PANEL -->
  <div style="display:flex;border-bottom:1.5px solid #333;">
    <div style="flex:1;padding:8px 18px;border-right:1.5px solid #333;">
      <div style="font-size:10px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.5px;">Guest Details</div>
      <table style="width:100%;">
        ${guestDetailsRowsHtml}
      </table>
    </div>
    <div style="flex:1;padding:8px 18px;">
      <div style="font-size:10px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.5px;">Stay Details</div>
      <table style="width:100%;">
        <tr><td class="lbl">Room No</td><td class="val" style="text-align:right;font-weight:600;font-size:13px;">${(inv.room_numbers && inv.room_numbers.length ? inv.room_numbers.join(', ') : inv.room_number) || '-'}</td></tr>
        <tr><td class="lbl">Room Type</td><td class="val" style="text-align:right;">${inv.room_type_name || '-'}</td></tr>
        <tr><td class="lbl">Reservation No</td><td class="val" style="text-align:right;">${inv.reservation_no || '-'}</td></tr>
        <tr><td class="lbl">Check-In</td><td class="val" style="text-align:right;">${fmtDate(inv.checkin_datetime)}${fmtTime(inv.checkin_datetime) ? ' ' + fmtTime(inv.checkin_datetime) : ''}</td></tr>
        <tr><td class="lbl">Check-Out</td><td class="val" style="text-align:right;">${fmtDate(inv.checkout_datetime)}${fmtTime(inv.checkout_datetime) ? ' ' + fmtTime(inv.checkout_datetime) : ''}</td></tr>
        <tr><td class="lbl">No. of Nights</td><td class="val" style="text-align:right;font-weight:600;">${roomNts}</td></tr>
        ${ratePlanCode ? `<tr><td class="lbl">Rate Plan</td><td class="val" style="text-align:right;">${ratePlanCode}</td></tr>` : ''}
        <tr><td class="lbl">Meal Plan</td><td class="val" style="text-align:right;">${mealPlanLabel}</td></tr>
      </table>
    </div>
  </div>

  <!-- LINE ITEMS TABLE -->
  <div style="padding:8px 12px 4px;">
    <table style="width:100%;">
      <thead>
        <tr style="background:#e8ecf0;">
          <th class="tc" style="width:30px;font-size:10px;">S.No</th>
          <th class="tl" style="font-size:10px;">Date</th>
          <th class="tl" style="font-size:10px;">Description</th>
          <th class="tc" style="font-size:10px;">SAC</th>
          <th class="tc" style="font-size:10px;">Room</th>
          <th class="tr" style="font-size:10px;">Taxable Amt</th>
          <th class="tc" style="font-size:10px;">GST%</th>
          ${hasIgst
            ? `<th class="tr" style="font-size:10px;">IGST</th>`
            : `<th class="tr" style="font-size:10px;">CGST</th>
               <th class="tr" style="font-size:10px;">SGST</th>`
          }
          <th class="tr" style="font-size:10px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="${colCount}" style="padding:8px;border:1px solid #bbb;color:#888;text-align:center;">No charge items</td></tr>`}
      </tbody>
      <tfoot>
        <tr style="background:#e8ecf0;font-weight:700;">
          <td class="tr" colspan="${hasIgst ? 5 : 5}" style="font-size:10.5px;">Sub Total</td>
          <td class="tr" style="font-size:10.5px;">${toMoney(totalTaxableValue)}</td>
          <td class="tc" style="font-size:10.5px;"></td>
          ${hasIgst
            ? `<td class="tr" style="font-size:10.5px;">${toMoney(totalIgst)}</td>`
            : `<td class="tr" style="font-size:10.5px;">${toMoney(totalCgst)}</td>
               <td class="tr" style="font-size:10.5px;">${toMoney(totalSgst)}</td>`
          }
          <td class="tr" style="font-size:11px;">${toMoney(totalDebit)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- TAX SUMMARY + PAYMENT SUMMARY + TOTALS -->
  <div style="display:flex;border-top:1.5px solid #333;font-size:10.5px;">

    <!-- Left: Tax Summary -->
    <div style="flex:1;padding:8px 14px;border-right:1px solid #ccc;">
      <div style="font-size:9.5px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.5px;">Tax Summary</div>
      <table style="width:100%;border:1px solid #ccc;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="padding:3px 6px;text-align:left;font-size:9.5px;border:1px solid #ccc;">Rate</th>
            <th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">Taxable</th>
            ${hasIgst
              ? `<th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">IGST</th>`
              : `<th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">CGST</th>
                 <th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">SGST</th>`
            }
            <th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">Total Tax</th>
          </tr>
        </thead>
        <tbody>
          ${taxSummaryHtml || `<tr><td colspan="${hasIgst ? 4 : 5}" style="padding:3px 6px;font-size:9.5px;color:#888;border:1px solid #ccc;">No tax</td></tr>`}
        </tbody>
      </table>
    </div>

    <!-- Right: Grand Total + Payments -->
    <div style="width:250px;padding:8px 14px;">
      <table style="width:100%;font-size:10.5px;">
        <tr><td style="padding:3px 0;">Gross Total</td><td style="text-align:right;font-weight:700;padding:3px 0;">${toMoney(totalDebit)}</td></tr>
        ${paymentLines.length > 0 ? `
          <tr><td colspan="2" style="padding:4px 0 2px;font-size:9.5px;font-weight:700;color:#555;border-top:1px solid #ddd;">PAYMENTS</td></tr>
          ${paymentRowsHtml}
          <tr style="border-top:1px solid #ccc;"><td style="padding:3px 0;font-size:10px;">Total Paid</td><td style="text-align:right;padding:3px 0;font-size:10px;">${toMoney(totalPayments)}</td></tr>
        ` : ''}
        ${billedTransferLines.length > 0 ? `
          <tr><td colspan="2" style="padding:4px 0 2px;font-size:9.5px;font-weight:700;color:#555;border-top:1px solid #ddd;">ACCOUNT RECEIVABLE</td></tr>
          ${billedTransferRowsHtml}
        ` : ''}
        ${inv.payment_collected_mode ? `
          <tr><td colspan="2" style="padding:4px 0 2px;font-size:9.5px;font-weight:700;color:#555;border-top:1px solid #ddd;">PAYMENT ACTUALLY COLLECTED</td></tr>
          <tr><td style="padding:2px 0;font-size:10px;">${inv.payment_collected_mode}${inv.payment_collected_reference ? ` (Ref: ${inv.payment_collected_reference})` : ''}</td><td style="text-align:right;padding:2px 0;font-size:10px;">${toMoney(inv.payment_collected_amount)}</td></tr>
          <tr><td colspan="2" style="padding:0 0 3px;font-size:8.5px;color:#888;">Collected${inv.payment_collected_at ? ' ' + String(inv.payment_collected_at).slice(0, 10) : ''}${inv.payment_collected_remarks ? ` — ${inv.payment_collected_remarks}` : ''}</td></tr>
        ` : ''}
        ${Math.abs(roundOff) >= 0.01 ? `<tr><td style="padding:2px 0;font-size:10px;">Round Off</td><td style="text-align:right;padding:2px 0;font-size:10px;">${roundOff >= 0 ? '+' : ''}${roundOff.toFixed(2)}</td></tr>` : ''}
        <tr style="border-top:2px solid #333;">
          <td style="padding:5px 0;font-weight:700;font-size:12px;">Net Payable</td>
          <td style="text-align:right;font-weight:700;font-size:12px;padding:5px 0;">${toMoney(payableAmount > 0 ? payableAmount : 0)}</td>
        </tr>
      </table>
    </div>
  </div>

  <!-- AMOUNT IN WORDS -->
  <div style="padding:5px 18px;border-top:1.5px solid #333;font-size:10px;background:#f8f8f8;">
    <b>Amount in words:</b> ${numberToWords(payableAmount > 0 ? payableAmount : totalDebit)}
  </div>

  <!-- SETTLEMENT + PLACE OF SUPPLY -->
  <div style="display:flex;justify-content:space-between;padding:5px 18px;border-top:1px solid #ccc;font-size:10px;">
    <div><b>Settlement Mode:</b> ${inv.settlement_mode || '-'}</div>
    ${hotelState ? `<div><b>Place of Supply:</b> ${hotelState}${hotelStateCode ? ` (${hotelStateCode})` : ''}</div>` : ''}
  </div>

  <!-- TERMS & CONDITIONS -->
  <!-- REQ-18 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md) — per-property configurable
       text (Hotel Config -> Policy), default = the 3 lines this block used to
       hardcode. -->
  <div style="padding:6px 18px;border-top:1px solid #ddd;font-size:9px;color:#666;line-height:1.4;">
    <div style="font-weight:600;margin-bottom:2px;">Terms & Conditions:</div>
    ${String(hotel.invoice_terms_text || DEFAULT_INVOICE_TERMS_TEXT_FALLBACK).split('\n').map((line) => line.trim()).filter(Boolean).map((line) => `<div>${line}</div>`).join('')}
  </div>

  <!-- SYSTEM-GENERATED NOTICE (no signature — matches the server-side PDF/email invoice) -->
  <div style="text-align:center;padding:16px 18px 8px;border-top:1px solid #ddd;font-size:10.5px;color:#555;">
    This is a system-generated invoice and does not require a signature.
  </div>

  <!-- THANK YOU -->
  <div style="text-align:center;padding:6px 18px;border-top:1px solid #eee;font-size:10px;color:#555;font-style:italic;">
    Thank you for staying with us. We look forward to welcoming you again!
  </div>

</div>
</body></html>`;
};

export default function PmsInvoiceRegister({ token, selectedPropertyId, selectedProperty, permissions, role }) {
  // Amend modal — set to an invoice row to open it.
  const [amendInvoice, setAmendInvoice] = useState(null);
  const [reissueInvoice, setReissueInvoice] = useState(null);
  // REQ-10: correct a wrongly-selected settlement mode on a settled bill
  const [modeEditInvoice, setModeEditInvoice] = useState(null);
  const [modeForm, setModeForm] = useState({ new_mode: '', reason: '', reference_no: '' });
  const [modeBusy, setModeBusy] = useState(false);
  // Invoice-level payment collection tracking (CORPORATE/TRAVEL_AGENT/OTA)
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ collected_amount: '', collected_mode: '', collected_date: todayIso(), reference_no: '', remarks: '' });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  // Undo Check-Out — reopens the stay for correction; cancels the invoice + CN.
  const [undoInvoice, setUndoInvoice] = useState(null);
  const [undoReason, setUndoReason] = useState('');
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoResult, setUndoResult] = useState(null);
  const canShowAmend = role === 'admin' || role === 'hotel_admin' || Boolean(permissions?.invoice_amend) || Boolean(permissions?.hotel_admin);
  const headers = useCallback(() => {
    const chainId = selectedProperty?.chain_id || '';
    const propertyId = selectedProperty?.id || selectedPropertyId || '';
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(chainId ? { 'x-chain-id': chainId } : {}),
      ...(propertyId ? { 'x-property-id': propertyId } : {})
    };
  }, [token, selectedPropertyId, selectedProperty]);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    from_date: todayIso(),
    to_date: todayIso(),
    invoice_no: '',
    guest_name: '',
    room_number: '',
    settlement_mode: 'ALL',
    bill_to: 'ALL',
    corporate_id: '',
    travel_agent_id: ''
  });

  // ── Data state ────────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterOptions, setFilterOptions] = useState({ corporates: [], travel_agents: [] });

  // ── Invoice detail modal ──────────────────────────────────────────────────
  const [detailModal, setDetailModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [splitTab, setSplitTab] = useState(0);
  const [voidingLine, setVoidingLine] = useState(null); // ledger_id currently being voided

  // ── Post Charge (for CANCELLED invoices) ─────────────────────────────────
  const [postChargeVisible, setPostChargeVisible] = useState(false);
  const [postChargeLoading, setPostChargeLoading] = useState(false);
  const [postChargeForm, setPostChargeForm] = useState({
    posting_type: 'ROOM_TARIFF', revenue_desc: 'Room Tariff', base_amount: '', gst_percent: '5', posting_date: todayIso(), payment_mode: 'CASH'
  });
  const [postChargeMsg, setPostChargeMsg] = useState('');

  // ── Pending Generation (REQ-23) — checked-out stays with no invoice yet,
  // only ever non-empty when this property's Invoice Generation mode is
  // MANUAL. SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md §18.
  const [pendingStays, setPendingStays] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [generatingStayId, setGeneratingStayId] = useState(null);
  const [pendingError, setPendingError] = useState('');

  const loadPendingInvoiceStays = useCallback(() => {
    setPendingLoading(true);
    fetch('/api/v1/pms/stays/pending-invoice', { headers: headers() })
      .then(r => r.json())
      .then(data => { if (data && !data.error) setPendingStays(Array.isArray(data.stays) ? data.stays : []); })
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  }, [headers]);

  const generateInvoiceForStay = async (stayId) => {
    setGeneratingStayId(stayId); setPendingError('');
    try {
      const res = await fetch(`/api/v1/pms/stays/${stayId}/generate-invoice`, { method: 'POST', headers: headers() });
      const data = await res.json();
      // INVOICE_EXISTS: backend already dropped the stale pending entry — refresh so it disappears
      if (data?.code === 'INVOICE_EXISTS') { loadPendingInvoiceStays(); handleSearch(); }
      if (!res.ok) throw new Error(data?.error || 'Failed to generate invoice');
      loadPendingInvoiceStays();
      handleSearch();
    } catch (e) {
      setPendingError(e?.message || 'Failed to generate invoice');
    } finally {
      setGeneratingStayId(null);
    }
  };

  // ── Load filter options (corporates + travel agents) on mount ─────────────
  useEffect(() => {
    fetch('/api/v1/pms/invoice-filter-options', { headers: headers() })
      .then(r => r.json())
      .then(data => { if (data && !data.error) setFilterOptions(data); })
      .catch(() => {});
  }, [headers, selectedPropertyId]);

  useEffect(() => {
    loadPendingInvoiceStays();
  }, [loadPendingInvoiceStays, selectedPropertyId]);

  // ── Auto-load today on mount ──────────────────────────────────────────────
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId]);

  // syncDateRange: picking a From later than the To (or a To earlier than the
  // From) would leave an inverted range that silently returns "No invoices
  // found" — the other end is pulled along instead.
  const setFilter = (key, value) => setFilters(f => syncDateRange({ ...f, [key]: value }, 'from_date', 'to_date', key));

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.set('from_date', filters.from_date);
      if (filters.to_date)   params.set('to_date', filters.to_date);
      if (filters.invoice_no.trim())   params.set('invoice_no', filters.invoice_no.trim());
      if (filters.guest_name.trim())   params.set('guest_name', filters.guest_name.trim());
      if (filters.room_number.trim())  params.set('room_number', filters.room_number.trim());
      if (filters.settlement_mode !== 'ALL') params.set('settlement_mode', filters.settlement_mode);
      if (filters.bill_to !== 'ALL') {
        params.set('bill_to', filters.bill_to);
        if (filters.bill_to === 'CORPORATE' && filters.corporate_id)
          params.set('corporate_id', filters.corporate_id);
        if (filters.bill_to === 'TRAVEL_AGENT' && filters.travel_agent_id)
          params.set('travel_agent_id', filters.travel_agent_id);
      }
      const res = await fetch(`/api/v1/pms/invoices?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load invoices');
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitModeEdit = async () => {
    if (!modeEditInvoice) return;
    if (!modeForm.new_mode) { alert('Select the correct payment mode'); return; }
    if (!modeForm.reason.trim()) { alert('Please enter a reason for the correction'); return; }
    setModeBusy(true);
    try {
      const res = await fetch(`/api/v1/pms/invoices/${modeEditInvoice.invoice_id}/settlement-mode`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ new_mode: modeForm.new_mode, reason: modeForm.reason.trim(), reference_no: (modeForm.reference_no || '').trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to correct payment mode');
      setModeEditInvoice(null);
      await handleSearch();
    } catch (e) {
      alert(e.message || 'Failed to correct payment mode');
    } finally {
      setModeBusy(false);
    }
  };

  const openPaymentModal = (inv) => {
    // collected_amount means "amount received in THIS transaction" (the backend
    // adds it onto whatever was already collected) — it must NOT be pre-filled
    // with the remaining balance, or a second partial payment on the same
    // invoice would submit the remaining amount as if it were the increment,
    // silently under/over-crediting the corporate ledger. Leave it blank; the
    // pending balance is shown as helper text in the modal instead.
    setPaymentForm({
      collected_amount: '',
      collected_mode: inv.payment_collected_mode || '',
      collected_date: todayIso(),
      reference_no: inv.payment_collected_reference || '',
      remarks: ''
    });
    setPaymentError('');
    setPaymentInvoice(inv);
  };

  const submitPaymentCollection = async (markUncollected = false) => {
    if (!paymentInvoice) return;
    if (!markUncollected) {
      if (!Number(paymentForm.collected_amount) || Number(paymentForm.collected_amount) <= 0) {
        setPaymentError('Enter a valid collected amount'); return;
      }
      if (!paymentForm.collected_mode) { setPaymentError('Select the payment mode'); return; }
    }
    setPaymentBusy(true); setPaymentError('');
    try {
      const res = await fetch(`/api/v1/pms/invoices/${paymentInvoice.invoice_id}/payment-collection`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify(markUncollected ? { mark_uncollected: true } : {
          collected_amount: Number(paymentForm.collected_amount),
          collected_mode: paymentForm.collected_mode,
          collected_date: paymentForm.collected_date,
          reference_no: paymentForm.reference_no.trim(),
          remarks: paymentForm.remarks.trim()
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update payment collection');
      setPaymentInvoice(null);
      await handleSearch();
    } catch (e) {
      setPaymentError(e.message || 'Failed to update payment collection');
    } finally {
      setPaymentBusy(false);
    }
  };

  const submitUndoCheckout = async () => {
    if (!undoInvoice) return;
    if (!undoReason.trim()) { alert('Please enter a reason — Undo Check-Out is audited.'); return; }
    setUndoBusy(true);
    try {
      const res = await fetch(`/api/v1/pms/stays/${undoInvoice.stay_id}/undo-checkout`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ reason: undoReason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Undo Check-Out failed');
      setUndoResult(data);
      await handleSearch();
    } catch (e) {
      alert(e.message || 'Undo Check-Out failed');
    } finally {
      setUndoBusy(false);
    }
  };

  const handleClear = () => {
    setFilters({
      from_date: todayIso(), to_date: todayIso(),
      invoice_no: '', guest_name: '', room_number: '',
      settlement_mode: 'ALL', bill_to: 'ALL',
      corporate_id: '', travel_agent_id: ''
    });
  };

  // ── CSV Download ───────────────────────────────────────────────────────────
  const handleDownloadCSV = () => {
    if (!invoices.length) return;
    const cols = [
      { label: 'Invoice No',    key: (r) => r.invoice_no || '' },
      { label: 'Reservation No',key: (r) => r.reservation_no || '' },
      { label: 'Guest Name',    key: (r) => r.guest_name || '' },
      { label: 'Mobile',        key: (r) => r.guest_mobile || '' },
      { label: 'Room No',       key: (r) => r.room_number || '' },
      { label: 'Room Type',     key: (r) => r.room_type_name || '' },
      { label: 'Check-In',      key: (r) => r.checkin_datetime  ? String(r.checkin_datetime).slice(0, 16).replace('T', ' ')  : '' },
      { label: 'Check-Out',     key: (r) => r.checkout_datetime ? String(r.checkout_datetime).slice(0, 16).replace('T', ' ') : '' },
      // For a CORPORATE/TRAVEL_AGENT invoice (payment_collectible_amount set),
      // Debit/Credit/Balance mean "corporate receivable status" here, same
      // substitution as the on-screen grid — not the guest folio's own
      // settlement, which always closes to 0 at checkout regardless of
      // whether the corporate has actually paid. See the matching comment
      // on the grid render below.
      { label: 'Total Debit',   key: (r) => toMoney(r.total_debit) },
      { label: 'Total Credit',  key: (r) => toMoney(r.payment_collectible_amount != null ? (r.payment_collected_amount || 0) : r.total_credit) },
      { label: 'Balance',       key: (r) => toMoney(r.payment_collectible_amount != null ? (Number(r.payment_collectible_amount) - Number(r.payment_collected_amount || 0)) : r.balance) },
      { label: 'Late C/O Charge', key: (r) => toMoney(r.late_checkout_charge) },
      { label: 'Settlement Mode', key: (r) => r.settlement_mode || '' },
      { label: 'Bill To',       key: (r) => r.corporate_name ? `Corporate: ${r.corporate_name}` : r.agent_name ? `Travel Agent: ${r.agent_name}` : 'Guest' },
      { label: 'Status',        key: (r) => r.invoice_status || '' },
      { label: 'Collection Status', key: (r) => {
        if (r.payment_collectible_amount == null) return '';
        if (r.payment_collected_at) return 'Collected';
        const collected = Number(r.payment_collected_amount || 0);
        return collected > 0 ? 'Partially Collected' : 'Outstanding';
      } },
      { label: 'Billing Address', key: (r) => r.guest_billing_address || '' },
    ];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = cols.map(c => esc(c.label)).join(',');
    const rows   = invoices.map(r => cols.map(c => esc(c.key(r))).join(','));
    const csv    = [header, ...rows].join('\r\n');
    const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    a.href       = url;
    const from   = filters.from_date || todayIso();
    const to     = filters.to_date   || todayIso();
    a.download   = `InvoiceRegister_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Open invoice detail ────────────────────────────────────────────────────
  const openDetail = async (invoiceId) => {
    setDetailLoading(true);
    setSplitTab(0);
    setPostChargeVisible(false);
    setPostChargeMsg('');
    try {
      const res = await fetch(`/api/v1/pms/invoices/${invoiceId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load invoice');
      setDetailModal(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // Payment/settlement posting types that must never be voided from this UI
  const PAYMENT_TYPES = new Set(['ADVANCE', 'CHECKOUT_PAYMENT', 'REVERSAL', 'REFUND', 'CITY_LEDGER_PAYMENT', 'DEPOSIT']);

  const voidLedgerLine = async (line) => {
    if (!line?.ledger_id) return;
    const reason = window.prompt(
      `Void this posting?\n\nType: ${line.posting_type}\nAmount: ₹${toMoney(line.debit_amount)}\nDesc: ${line.revenue_desc || line.remarks || '-'}\n\nEnter reason for voiding (required):`
    );
    if (!reason || !reason.trim()) return;
    setVoidingLine(line.ledger_id);
    try {
      const res = await fetch(`/api/v1/pms/folio-ledger/${line.ledger_id}/void`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ reason: reason.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Void failed');
      // Reload the modal to show updated folio lines
      await openDetail(detailModal.invoice_id);
    } catch (err) {
      alert(`Void failed: ${err.message}\n\nIf the invoice is FINAL, use Amend → Cancel & Reissue first to unlock the folio, then void this line.`);
    } finally {
      setVoidingLine(null);
    }
  };

  const doPostCharge = async () => {
    const base = Number(postChargeForm.base_amount);
    if (!base || base <= 0) { setPostChargeMsg('Enter a valid base amount.'); return; }
    if (!postChargeForm.revenue_desc.trim()) { setPostChargeMsg('Description is required.'); return; }
    setPostChargeLoading(true);
    setPostChargeMsg('');
    try {
      const res = await fetch(`/api/v1/pms/invoices/${detailModal.invoice_id}/post-charge`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          posting_type: postChargeForm.posting_type,
          revenue_desc: postChargeForm.revenue_desc.trim(),
          base_amount: base,
          gst_percent: Number(postChargeForm.gst_percent),
          posting_date: postChargeForm.posting_date,
          payment_mode: postChargeForm.payment_mode
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to post charge');
      setPostChargeVisible(false);
      setPostChargeMsg('');
      await openDetail(detailModal.invoice_id);
    } catch (err) {
      setPostChargeMsg(err.message);
    } finally {
      setPostChargeLoading(false);
    }
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = (inv, printLines, splitLabel, copyLabel) => {
    if (!inv) return;
    const html = buildInvoiceHtml(inv, printLines, splitLabel, copyLabel || 'Original');
    const popup = window.open('', '_blank', 'width=900,height=700');
    if (!popup) return;
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  // ── WhatsApp share (actual PDF document, Bug 8649) ────────────────────────
  // Mint a public token-based PDF URL, then prefer the Web Share API so the
  // guest receives the real invoice PDF file through WhatsApp (mobile). Falls
  // back to a wa.me text message + download link where file-share isn't
  // supported (most desktop browsers).
  const shareWhatsApp = async (inv) => {
    if (!inv) return;
    let downloadUrl = '';
    try {
      const tr = await fetch(`/api/v1/pms/invoices/${inv.invoice_id}/generate-pdf-token`, {
        method: 'POST', headers: headers(),
      });
      if (tr.ok) {
        const td = await tr.json();
        downloadUrl = td.download_url || '';
      }
    } catch (_) {}

    const summary = [
      `*Invoice: ${inv.invoice_no || '-'}*`,
      `Guest: ${inv.guest_name || '-'}`,
      `Room: ${(inv.room_numbers && inv.room_numbers.length ? inv.room_numbers.join(', ') : inv.room_number) || '-'}`,
      `Check-In: ${inv.checkin_datetime ? String(inv.checkin_datetime).slice(0,10) : '-'}`,
      `Check-Out: ${inv.checkout_datetime ? String(inv.checkout_datetime).slice(0,10) : '-'}`,
      `Total: Rs.${toMoney(inv.total_debit)}`,
      `Settlement: ${inv.settlement_mode || '-'}`,
    ].join('\n');

    // Preferred: share the actual PDF file (Web Share API, level 2 — files).
    if (downloadUrl && typeof navigator !== 'undefined' && typeof navigator.canShare === 'function') {
      try {
        const pdfRes = await fetch(downloadUrl);
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          const file = new File([blob], `Invoice-${inv.invoice_no || inv.invoice_id}.pdf`, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({ files: [file], title: `Invoice ${inv.invoice_no || ''}`, text: summary });
            } catch (_) { /* user dismissed the share sheet — nothing more to do */ }
            return;
          }
        }
      } catch (_) { /* fetch/blob unavailable — fall through to the link below */ }
    }

    // Fallback: WhatsApp click-to-chat with summary text + a PDF download link.
    const text = downloadUrl ? `${summary}\n\nDownload Invoice PDF:\n${downloadUrl}` : summary;
    const mobile = String(inv.guest_mobile || '').replace(/\D/g, '');
    window.open(`${mobile ? `https://wa.me/${mobile}` : 'https://wa.me/'}?text=${encodeURIComponent(text)}`, '_blank');
  };

  // ── Refund modal state ────────────────────────────────────────────────────
  const [refundModal, setRefundModal] = useState(null); // invoice object
  const [refundForm, setRefundForm] = useState({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' });
  const [refundLoading, setRefundLoading] = useState(false);

  const doRefund = async () => {
    if (!refundModal) return;
    const amount = Number(refundForm.refund_amount);
    if (!amount || amount <= 0) { alert('Enter a valid refund amount'); return; }
    if (!refundForm.refund_mode) { alert('Select a refund mode'); return; }
    if (!String(refundForm.refund_reason || '').trim()) { alert('Refund reason is required'); return; }
    setRefundLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/invoices/${refundModal.invoice_id}/refund`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ refund_amount: amount, refund_mode: refundForm.refund_mode, refund_reason: refundForm.refund_reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record refund');
      setRefundModal(null);
      setRefundForm({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' });
      alert(`Refund of ₹${amount.toFixed(2)} recorded for Invoice #${refundModal.invoice_number || refundModal.invoice_id}`);
    } catch (err) {
      alert(`Refund failed: ${err.message}`);
    } finally {
      setRefundLoading(false);
    }
  };

  // ── Email share (backend sends HTML invoice email) ─────────────────────────
  const [emailSending, setEmailSending] = useState(null); // invoice_id being sent
  const shareEmail = async (inv) => {
    if (!inv) return;
    const toAddr = inv.guest_email || inv.guest_detail?.guest_email || '';
    if (!toAddr) {
      const manual = window.prompt('No email on file. Enter guest email address:');
      if (!manual || !manual.includes('@')) return;
      inv = { ...inv, guest_email: manual };
    }
    setEmailSending(inv.invoice_id);
    try {
      const res = await fetch(`/api/v1/pms/invoices/${inv.invoice_id}/send-email`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inv.guest_email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send email');
      alert(`Invoice emailed to ${data.sent_to}`);
    } catch (err) {
      alert(`Email failed: ${err.message}`);
    } finally {
      setEmailSending(null);
    }
  };

  const inputStyle = { padding: '0.45rem 0.6rem', borderRadius: '5px', border: '1px solid #dcdde1', fontSize: '0.875rem' };
  const tabBtnStyle = (active) => ({ padding: '0.2rem 0.7rem', borderRadius: '4px', border: '1px solid #cbd5e0', background: active ? '#2b6cb0' : '#fff', color: active ? '#fff' : '#2d3748', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' });

  // ── Detail modal computed ─────────────────────────────────────────────────
  const allDetailLines = Array.isArray(detailModal?.lines) ? detailModal.lines : [];
  const splitNos = [...new Set(allDetailLines.map(l => Number(l.split_no || 1)))].sort((a, b) => a - b);
  const hasSplits = splitNos.length > 1;
  const activeLines = hasSplits && splitTab > 0 ? allDetailLines.filter(l => Number(l.split_no || 1) === splitTab) : allDetailLines;
  const activeDebit  = activeLines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
  const activeCredit = activeLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
  // Net Payable must exclude a CHECKOUT_CITY_LEDGER_TRANSFER/FOLIO_ROUTE_TRANSFER
  // credit (billed to the corporate/agent's own account, not a real payment) --
  // see BILLED_TRANSFER_TYPES at module scope. activeCredit above is left as
  // the true ledger total (used in the Totals row); this is only for Net Payable.
  const activeRealCredit = activeLines.reduce((s, l) => s + (BILLED_TRANSFER_TYPES.has(String(l.posting_type || '').toUpperCase()) ? 0 : Number(l.credit_amount || 0)), 0);
  const splitLabel   = hasSplits && splitTab > 0 ? `Split ${splitTab}` : null;

  return (
    <div className="page-shell">
      {/* Breadcrumb */}
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Front Office</span> <span style={{ color: '#cbd5e0' }}>›</span>{' '}
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Invoices</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.25rem' }}>Invoice Register</h2>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>View, print and share guest invoices.</p>

      {error && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error}</div>}

      {/* ── Pending Generation (REQ-23) — only ever non-empty under MANUAL
          invoice generation mode. SRS §18. ── */}
      {(pendingStays.length > 0 || pendingLoading) && (
        <div style={{ border: '1px solid #f6ad55', background: '#fffaf0', borderRadius: '8px', padding: '0.9rem 1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 700, color: '#9c4221', marginBottom: '0.4rem' }}>
            ⏳ Pending Generation {pendingStays.length > 0 ? `(${pendingStays.length})` : ''}
          </div>
          <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.82rem', color: '#7b341e' }}>
            These guests have checked out but no invoice has been generated yet (Invoice Generation is set to Manual for this property — see Hotel Config → Policy).
          </p>
          {pendingError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{pendingError}</div>}
          {pendingLoading ? (
            <div style={{ color: '#7b341e', fontSize: '0.85rem' }}>Loading...</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.4rem' }}>
              {pendingStays.map((s) => (
                <div key={s.stay_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #fbd38d', borderRadius: '6px', padding: '0.5rem 0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.85rem' }}>
                    <b>{s.reservation_no || `#${s.reservation_id}`}</b> — {s.primary_guest_name || 'Guest'} · Room {s.room_number || '-'} ({s.room_type_name || '-'})
                    <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                      Checked out {s.actual_checkout_datetime ? `${fmtDateDMY(s.actual_checkout_datetime)} ${fmtTimeHHMM(s.actual_checkout_datetime)}` : '-'}
                    </div>
                  </div>
                  <button
                    onClick={() => generateInvoiceForStay(s.stay_id)}
                    disabled={generatingStayId === s.stay_id}
                    className="btn-primary"
                    style={{ padding: '0.4rem 1rem', fontSize: '0.82rem', opacity: generatingStayId === s.stay_id ? 0.7 : 1 }}
                  >
                    {generatingStayId === s.stay_id ? 'Generating...' : 'Generate Invoice'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem' }}>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            From Date
            <input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)} style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            To Date
            {/* min: the native picker also greys out anything before From Date */}
            <input type="date" value={filters.to_date} min={filters.from_date || undefined} onChange={e => setFilter('to_date', e.target.value)} style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            Invoice No
            <input value={filters.invoice_no} onChange={e => setFilter('invoice_no', e.target.value)} placeholder="INV-..." style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            Guest Name
            <input value={filters.guest_name} onChange={e => setFilter('guest_name', e.target.value)} placeholder="Search..." style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            Room No
            <input value={filters.room_number} onChange={e => setFilter('room_number', e.target.value)} placeholder="101..." style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
            Settlement
            <select value={filters.settlement_mode} onChange={e => setFilter('settlement_mode', e.target.value)} style={inputStyle}>
              <option value="ALL">All</option>
              <option value="CASH">Cash</option>
              <option value="CARD">Card</option>
              <option value="UPI">UPI</option>
              <option value="BANK">Bank</option>
              <option value="CORPORATE">Corporate</option>
            </select>
          </label>

        </div>

        {/* Bill To row */}
        <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>Bill To:</span>
          {['ALL', 'GUEST', 'CORPORATE', 'TRAVEL_AGENT'].map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.84rem', cursor: 'pointer' }}>
              <input type="radio" name="bill_to" value={opt} checked={filters.bill_to === opt}
                onChange={() => setFilter('bill_to', opt)} />
              {opt === 'ALL' ? 'All' : opt === 'TRAVEL_AGENT' ? 'Travel Agent' : opt.charAt(0) + opt.slice(1).toLowerCase()}
            </label>
          ))}

          {filters.bill_to === 'CORPORATE' && (
            <select value={filters.corporate_id} onChange={e => setFilter('corporate_id', e.target.value)} style={{ ...inputStyle, minWidth: '180px' }}>
              <option value="">All Corporates</option>
              {filterOptions.corporates.map(c => (
                <option key={c.corporate_id} value={c.corporate_id}>{c.corporate_name}</option>
              ))}
            </select>
          )}

          {filters.bill_to === 'TRAVEL_AGENT' && (
            <select value={filters.travel_agent_id} onChange={e => setFilter('travel_agent_id', e.target.value)} style={{ ...inputStyle, minWidth: '180px' }}>
              <option value="">All Travel Agents</option>
              {filterOptions.travel_agents.map(a => (
                <option key={a.travel_agent_id} value={a.travel_agent_id}>{a.agent_name}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="btn-primary" onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button onClick={handleClear} style={{ padding: '0.45rem 1rem', borderRadius: '5px', border: '1px solid #dcdde1', background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
            Clear
          </button>
          <button
            onClick={handleDownloadCSV}
            disabled={!invoices.length}
            title={invoices.length ? `Download ${invoices.length} invoice(s) as CSV` : 'No data to download'}
            style={{ padding: '0.45rem 1rem', borderRadius: '5px', border: '1px solid #48bb78', background: invoices.length ? '#f0fff4' : '#f7fafc', color: invoices.length ? '#276749' : '#a0aec0', cursor: invoices.length ? 'pointer' : 'not-allowed', fontSize: '0.875rem', fontWeight: 600 }}
          >
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {/* ── Results ── */}
      <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.4rem' }}>
        {loading ? 'Loading...' : `Showing ${invoices.length} invoice(s)`}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Invoice No</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Guest</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Corporate / Agent</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Room</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', whiteSpace: 'nowrap' }}>C/O Date</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Debit</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Credit</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Balance</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Mode</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, idx) => {
              const isVoid = String(inv.invoice_status || '').toUpperCase() === 'VOID';
              // The invoice's own settlement (settled_corporate_name/settled_agent_name,
              // set at checkout or corrected later) is the true source of truth for who
              // it's billed to — not the reservation's booking-time corporate_name/
              // agent_name, which stays null for a direct/guest booking later settled
              // to Corporate/Travel Agent at checkout. Falling back to the booking-time
              // name only covers the (rare) case where settlement was never recorded.
              const billToCorporate = inv.settled_corporate_name || inv.corporate_name;
              const billToAgent = inv.settled_agent_name || inv.agent_name;
              const billTo = billToCorporate ? `🏢 ${billToCorporate}` : billToAgent ? `✈ ${billToAgent}` : '';
              // Debit/Credit/Balance below are the GUEST FOLIO's own settlement — a
              // corporate/TA invoice always closes the guest's folio to 0 at checkout
              // by transferring the balance to that party's ledger, which is correct
              // for the folio but reads exactly like "the corporate already paid the
              // hotel" if shown as-is. When payment_collectible_amount is set (only
              // true for CORPORATE/TRAVEL_AGENT invoices that went through that
              // transfer), show the actual corporate-receivable state instead: what's
              // billed, what's really been collected from the corporate, and what's
              // still outstanding. OTA/direct-tender rows never set this field, so
              // they keep showing the folio figures unchanged — correct for them,
              // since there's no separate party ledger involved.
              const isBilledReceivable = inv.payment_collectible_amount != null;
              const collectibleAmount = Number(inv.payment_collectible_amount || 0);
              const collectedAmount = Number(inv.payment_collected_amount || 0);
              const displayDebit = inv.total_debit;
              const displayCredit = isBilledReceivable ? collectedAmount : inv.total_credit;
              const displayBalance = isBilledReceivable ? Number((collectibleAmount - collectedAmount).toFixed(2)) : inv.balance;
              return (
                <tr key={inv.invoice_id} style={{ borderTop: '1px solid #edf2f7', background: isVoid ? '#fff5f5' : idx % 2 === 0 ? '#fff' : '#f7fafc' }}>
                  <td style={{ padding: '0.45rem 0.6rem', fontWeight: 600, color: '#2b6cb0', whiteSpace: 'nowrap' }}>{inv.invoice_no}</td>
                  <td style={{ padding: '0.45rem 0.6rem' }}>{inv.guest_name || '-'}</td>
                  <td style={{ padding: '0.45rem 0.6rem', fontSize: '0.8rem', color: '#4a5568' }}>{billTo || '-'}</td>
                  <td style={{ padding: '0.45rem 0.6rem' }}>{inv.room_number || '-'}</td>
                  <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {inv.checkout_datetime ? String(inv.checkout_datetime).slice(0, 10) : '-'}
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>{toMoney(displayDebit)}</td>
                  <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>{toMoney(displayCredit)}</td>
                  <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: Number(displayBalance || 0) > 0 ? '#c53030' : '#276749', fontWeight: 600 }}>
                    {toMoney(displayBalance)}
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', fontSize: '0.8rem' }}>
                    {inv.settlement_mode || '-'}
                    {/* Collected/Partially Collected/Outstanding tracks whether the hotel
                        has been paid back by the corporate/agent/OTA for this invoice — a
                        receivable that only makes sense for a live invoice. A CANCELLED
                        (or VOID) invoice can never be marked collected (PATCH
                        /invoices/:id/payment-collection refuses it outright), so showing
                        it here just left every cancelled corporate/agent booking stuck
                        looking like an unpaid receivable forever. */}
                    {!isVoid && String(inv.invoice_status || '').toUpperCase() !== 'CANCELLED'
                      && ['CORPORATE', 'TRAVEL_AGENT', 'OTA'].includes(String(inv.settlement_mode || '').toUpperCase()) && (
                      <div style={{ marginTop: '0.2rem' }}>
                        {inv.payment_collected_at ? (
                          <span title={`Collected ${String(inv.payment_collected_at).slice(0, 10)}`} style={{ background: '#c6f6d5', color: '#276749', padding: '0.05rem 0.4rem', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700 }}>
                            ✓ Collected {String(inv.payment_collected_at).slice(0, 10)}
                          </span>
                        ) : collectedAmount > 0 ? (
                          <span title={`Collected ${toMoney(collectedAmount)} of ${toMoney(collectibleAmount)}`} style={{ background: '#fed7aa', color: '#9a3412', padding: '0.05rem 0.4rem', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700 }}>
                            ◐ Partial — {toMoney(collectedAmount)} of {toMoney(collectibleAmount)}
                          </span>
                        ) : (
                          <span style={{ background: '#fef3c7', color: '#92400e', padding: '0.05rem 0.4rem', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700 }}>
                            ⏳ Outstanding
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}>
                    {isVoid ? (
                      <span style={{ background: '#fed7d7', color: '#c53030', padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>VOID</span>
                    ) : String(inv.invoice_status || '').toUpperCase() === 'CANCELLED' ? (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                        {inv.superseded_by_invoice_id ? 'SUPERSEDED' : 'CANCELLED'}
                      </span>
                    ) : Number(inv.narrative_amend_count || 0) > 0 ? (
                      <span title="Narrative amendment(s) applied — tax math unchanged"
                            style={{ background: '#dbeafe', color: '#1e40af', padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                        AMENDED ×{inv.narrative_amend_count}
                      </span>
                    ) : (
                      <span style={{ background: '#c6f6d5', color: '#276749', padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>FINAL</span>
                    )}
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                      {/* View */}
                      <button
                        title="View Invoice"
                        onClick={() => openDetail(inv.invoice_id)}
                        disabled={detailLoading}
                        style={{ background: 'none', border: '1px solid #bee3f8', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: '#2b6cb0' }}
                      >👁</button>
                      {/* Print */}
                      <button
                        title="Print Invoice"
                        onClick={async () => {
                          const res = await fetch(`/api/v1/pms/invoices/${inv.invoice_id}`, { headers: headers() });
                          const data = await res.json();
                          if (res.ok) handlePrint(data, data.lines, null, 'Original');
                        }}
                        style={{ background: 'none', border: '1px solid #c6f6d5', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: '#276749' }}
                      >🖨</button>
                      {/* WhatsApp */}
                      <button
                        title="Share invoice PDF on WhatsApp"
                        onClick={() => shareWhatsApp(inv)}
                        style={{ background: 'none', border: '1px solid #c6f6d5', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: '#25d366' }}
                      >📱</button>
                      {/* Email */}
                      <button
                        title="Email invoice PDF to guest"
                        onClick={() => shareEmail(inv)}
                        disabled={emailSending === inv.invoice_id}
                        style={{ background: 'none', border: '1px solid var(--theme-border)', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: emailSending === inv.invoice_id ? 'not-allowed' : 'pointer', fontSize: '0.9rem', color: '#4a5568', opacity: emailSending === inv.invoice_id ? 0.5 : 1 }}
                      >{emailSending === inv.invoice_id ? '⏳' : '✉'}</button>
                      {/* Amend (Phase 1+ of invoice amendment feature). Hidden if user lacks permission. */}
                      {canShowAmend && !isVoid && String(inv.invoice_status || '').toUpperCase() !== 'CANCELLED' && (
                        <button
                          title="Amend invoice (narrative edit or Cancel & Reissue)"
                          onClick={() => setAmendInvoice(inv)}
                          style={{ background: 'none', border: '1px solid #fed7aa', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: '#c2410c' }}
                        >✏</button>
                      )}
                      {/* REQ-10: correct wrongly-selected settlement mode. Direct tenders can always
                          swap; a billed mode (CORPORATE/TRAVEL_AGENT/CITY_LEDGER) can also swap to a
                          direct tender IF checkout never actually posted a real receivable for it —
                          the backend verifies that against pms_city_ledger_txn and refuses otherwise. */}
                      {canShowAmend && !isVoid && String(inv.invoice_status || '').toUpperCase() !== 'CANCELLED'
                        && ['CASH', 'CARD', 'UPI', 'CHEQUE', 'NEFT', 'CORPORATE', 'TRAVEL_AGENT', 'CITY_LEDGER'].includes(String(inv.settlement_mode || '').toUpperCase()) && (
                        <button
                          title={`Correct payment mode (currently ${inv.settlement_mode})`}
                          onClick={() => { setModeForm({ new_mode: '', reason: '', reference_no: '' }); setModeEditInvoice(inv); }}
                          style={{ background: 'none', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.82rem', color: '#1d4ed8', fontWeight: 600 }}
                        >₹ Mode</button>
                      )}
                      {/* Update Payment — record/correct if & when a CORPORATE/TRAVEL_AGENT/OTA
                          receivable was actually collected. Not gated by canShowAmend — this
                          records an operational fact, not a document amendment. */}
                      {!isVoid && String(inv.invoice_status || '').toUpperCase() !== 'CANCELLED'
                        && ['CORPORATE', 'TRAVEL_AGENT', 'OTA'].includes(String(inv.settlement_mode || '').toUpperCase()) && (
                        <button
                          title="Update payment collection for this invoice"
                          onClick={() => openPaymentModal(inv)}
                          style={{ background: 'none', border: '1px solid #fde68a', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.82rem', color: '#92400e', fontWeight: 600 }}
                        >💰 Payment</button>
                      )}
                      {/* Undo Check-Out — reopens the stay so everything (tariff,
                          guest details, extra charges) can be corrected and the
                          guest checked out again with a fresh invoice. Only for a
                          live invoice whose stay is still CHECKED_OUT. */}
                      {canShowAmend && !isVoid && String(inv.invoice_status || '').toUpperCase() !== 'CANCELLED'
                        && inv.stay_id && String(inv.stay_status || '').toUpperCase() === 'CHECKED_OUT' && (
                        <button
                          title="Undo Check-Out — reopen the stay to correct it, then check out again for a new invoice"
                          onClick={() => { setUndoReason(''); setUndoResult(null); setUndoInvoice(inv); }}
                          style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#92400e', fontWeight: 600 }}
                        >↩ Undo C/O</button>
                      )}
                      {/* Reissue — only shown for CANCELLED invoices not yet replaced. */}
                      {canShowAmend && String(inv.invoice_status || '').toUpperCase() === 'CANCELLED' && !inv.superseded_by_invoice_id && (
                        <button
                          title="Issue replacement invoice for this cancelled one"
                          onClick={() => setReissueInvoice(inv)}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#b91c1c', fontWeight: 600 }}
                        >Reissue</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan={11} style={{ padding: '1.5rem', textAlign: 'center', color: '#718096' }}>No invoices found for the selected filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── REQ-10: Correct Payment Mode Modal ── */}
      {modeEditInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onMouseDown={(e) => { if (e.target === e.currentTarget && !modeBusy) setModeEditInvoice(null); }}>
          <div style={{ width: 'min(440px, 96vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.1rem 1.2rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.3rem', color: 'var(--theme-primary-dark)' }}>Correct Payment Mode</h3>
            <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.9rem' }}>
              Invoice <b>{modeEditInvoice.invoice_no || `#${modeEditInvoice.invoice_id}`}</b> — currently settled as <b>{modeEditInvoice.settlement_mode}</b>. The amount is unchanged; this only corrects the tender.
            </div>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Correct mode
              <select value={modeForm.new_mode} onChange={(e) => setModeForm({ ...modeForm, new_mode: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }}>
                <option value="">-- select --</option>
                {['CASH', 'CARD', 'UPI', 'CHEQUE', 'NEFT'].filter(m => m !== String(modeEditInvoice.settlement_mode || '').toUpperCase()).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Reference No <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — transaction/card/auth no. for the corrected mode)</span>
              <input value={modeForm.reference_no} onChange={(e) => setModeForm({ ...modeForm, reference_no: e.target.value })} placeholder="e.g. UPI transaction ID" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '1rem' }}>Reason (audited)
              <input value={modeForm.reason} onChange={(e) => setModeForm({ ...modeForm, reason: e.target.value })} placeholder="e.g. Front desk selected Cash by mistake" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => setModeEditInvoice(null)} disabled={modeBusy} style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem' }}>Cancel</button>
              <button onClick={submitModeEdit} disabled={modeBusy} style={{ padding: '0.45rem 1rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, cursor: modeBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: modeBusy ? 0.6 : 1 }}>{modeBusy ? 'Saving…' : 'Correct Mode'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Update Payment (invoice-level collection tracking) Modal ── */}
      {paymentInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onMouseDown={(e) => { if (e.target === e.currentTarget && !paymentBusy) setPaymentInvoice(null); }}>
          <div style={{ width: 'min(460px, 96vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.1rem 1.2rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.3rem', color: '#92400e' }}>💰 Record Corporate Payment</h3>
            <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.9rem' }}>
              Invoice <b>{paymentInvoice.invoice_no || `#${paymentInvoice.invoice_id}`}</b> — settled to{' '}
              <b>{paymentInvoice.settled_corporate_name || paymentInvoice.settled_agent_name || paymentInvoice.settlement_mode}</b>.{' '}
              {paymentInvoice.payment_collectible_amount != null ? (() => {
                const collectible = Number(paymentInvoice.payment_collectible_amount);
                const collected = Number(paymentInvoice.payment_collected_amount || 0);
                const pending = Number((collectible - collected).toFixed(2));
                return (
                  <>
                    Billed: <b>{toMoney(collectible)}</b>
                    {collected > 0 ? <> · Already collected: <b>{toMoney(collected)}</b></> : null}
                    {' '}· Pending: <b style={{ color: pending > 0 ? '#c53030' : '#276749' }}>{toMoney(pending)}</b>.
                  </>
                );
              })() : <>Collectible amount not on record for this invoice — enter what was actually collected.</>}
            </div>
            {paymentError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{paymentError}</div>}
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Amount Received Now <span style={{ fontWeight: 400, color: '#94a3b8' }}>(this transaction only — partial is fine, it adds to any amount already collected)</span>
              <input type="number" min="0" step="0.01" value={paymentForm.collected_amount} onChange={(e) => setPaymentForm({ ...paymentForm, collected_amount: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Payment Mode
              <select value={paymentForm.collected_mode} onChange={(e) => setPaymentForm({ ...paymentForm, collected_mode: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }}>
                <option value="">-- select --</option>
                {['BANK_TRANSFER', 'NEFT', 'RTGS', 'CHEQUE', 'UPI', 'ONLINE', 'CASH', 'OTHER'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Collected Date
              <input type="date" value={paymentForm.collected_date} onChange={(e) => setPaymentForm({ ...paymentForm, collected_date: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.7rem' }}>Reference No <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              <input value={paymentForm.reference_no} onChange={(e) => setPaymentForm({ ...paymentForm, reference_no: e.target.value })} placeholder="e.g. NEFT UTR / cheque no." style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.8rem' }}>Remarks <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              <input value={paymentForm.remarks} onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.9rem' }}>
              Recording here updates this invoice and frees up the party's outstanding balance. Don't also record the same payment via Masters → Corporate/Travel Agent → Record Payment — that would double-count it.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
              {paymentInvoice.payment_collected_at ? (
                <button onClick={() => submitPaymentCollection(true)} disabled={paymentBusy} style={{ padding: '0.45rem 0.8rem', border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>Mark still outstanding</button>
              ) : <span />}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setPaymentInvoice(null)} disabled={paymentBusy} style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem' }}>Cancel</button>
                <button onClick={() => submitPaymentCollection(false)} disabled={paymentBusy} style={{ padding: '0.45rem 1rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, cursor: paymentBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: paymentBusy ? 0.6 : 1 }}>{paymentBusy ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Undo Check-Out Modal ── */}
      {undoInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onMouseDown={(e) => { if (e.target === e.currentTarget && !undoBusy) setUndoInvoice(null); }}>
          <div style={{ width: 'min(520px, 96vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.1rem 1.2rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.3rem', color: '#92400e' }}>↩ Undo Check-Out</h3>

            {undoResult ? (
              <>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '0.7rem', fontSize: '0.85rem', color: '#166534', marginBottom: '0.8rem' }}>
                  <b>{undoResult.message}</b>
                  <div style={{ marginTop: '0.4rem' }}>
                    Room <b>{undoResult.room_number || '-'}</b> · {undoResult.reservation_no || ''}
                    {undoResult.credit_note_no ? <> · Credit note <b>{undoResult.credit_note_no}</b></> : null}
                  </div>
                </div>
                {Array.isArray(undoResult.warnings) && undoResult.warnings.length > 0 && (
                  <ul style={{ margin: '0 0 0.9rem', paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#78350f' }}>
                    {undoResult.warnings.map((w, i) => <li key={i} style={{ marginBottom: 3 }}>{w}</li>)}
                  </ul>
                )}
                <div style={{ fontSize: '0.83rem', color: '#4a5568', marginBottom: '0.9rem' }}>
                  Next: correct the stay (tariff, guest details, extra charges) from the Command Center or the reservation, then check the guest out again — a new invoice is generated.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setUndoInvoice(null); setUndoResult(null); }} style={{ padding: '0.45rem 1rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 }}>Done</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.7rem' }}>
                  Invoice <b>{undoInvoice.invoice_no || `#${undoInvoice.invoice_id}`}</b> · Room <b>{undoInvoice.room_number || '-'}</b> · <b>{undoInvoice.guest_name || '-'}</b>
                </div>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '0.7rem', fontSize: '0.82rem', color: '#78350f', marginBottom: '0.8rem' }}>
                  This reopens the stay so you can fix anything on it:
                  <ul style={{ margin: '0.4rem 0 0.5rem', paddingLeft: '1.1rem' }}>
                    <li>Guest goes back to <b>Checked-In</b>, room back to <b>Occupied</b></li>
                    <li>The <b>check-out date/time stays as it is</b> — only what you correct changes</li>
                    <li>Folio <b>reopens</b> — charges can be voided and re-posted</li>
                    <li>This invoice is <b>cancelled</b> and a credit note is issued</li>
                    <li>Payments already collected <b>stay on the folio</b> — re-checkout asks only for the difference</li>
                  </ul>
                  Not reversible in one click — you check the guest out again to produce the corrected invoice.
                </div>
                <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '1rem' }}>Reason (audited)
                  <input value={undoReason} onChange={(e) => setUndoReason(e.target.value)} placeholder="e.g. Room tariff billed at 2095.24, should be 2200 incl. GST" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button onClick={() => setUndoInvoice(null)} disabled={undoBusy} style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem' }}>Cancel</button>
                  <button onClick={submitUndoCheckout} disabled={undoBusy} style={{ padding: '0.45rem 1rem', border: 'none', background: '#b45309', color: '#fff', borderRadius: 6, cursor: undoBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: undoBusy ? 0.6 : 1 }}>{undoBusy ? 'Undoing…' : 'Undo Check-Out'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Invoice Amend Modal (Phase 1+) ── */}
      {amendInvoice && (
        <InvoiceAmendModal
          token={token}
          invoice={amendInvoice}
          selectedProperty={selectedProperty}
          onClose={() => setAmendInvoice(null)}
          onAmended={() => { handleSearch(); }}
        />
      )}

      {/* ── Invoice Reissue Modal — for CANCELLED-not-yet-replaced invoices ── */}
      {reissueInvoice && (
        <InvoiceReissueModal
          token={token}
          invoice={reissueInvoice}
          selectedProperty={selectedProperty}
          onClose={() => setReissueInvoice(null)}
          onReissued={() => { handleSearch(); }}
        />
      )}

      {/* ── Invoice Detail Modal ── */}
      {detailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ width: 'min(860px,97vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setDetailModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '4px', padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Invoice Detail</h4>

            <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.7rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 0.8rem', fontSize: '0.9rem' }}>
              <div><b>Invoice No:</b> {detailModal.invoice_no || '-'}</div>
              <div><b>Reservation:</b> {detailModal.reservation_no || '-'}</div>
              {(!detailModal.corporate_detail || detailModal.corporate_detail.invoice_show_guest_name !== false) && (
                <div><b>Guest:</b> {detailModal.guest_name || '-'}</div>
              )}
              <div><b>Mobile:</b> {detailModal.guest_mobile || '-'}</div>
              {(!detailModal.corporate_detail || detailModal.corporate_detail.invoice_show_guest_name !== false)
                && Array.isArray(detailModal.additional_guests) && detailModal.additional_guests.length > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <b>Other Guest(s):</b> {detailModal.additional_guests.map((g) => g.guest_name).filter(Boolean).join(', ')}
                </div>
              )}
              {detailModal.corporate_detail?.corporate_name && (
                <>
                  {detailModal.corporate_detail.invoice_show_company_name !== false && (
                    <>
                      <div><b>Corporate:</b> {detailModal.corporate_detail.corporate_name}</div>
                      {detailModal.corporate_detail.corporate_code && <div><b>Corp. Code:</b> {detailModal.corporate_detail.corporate_code}</div>}
                    </>
                  )}
                  {detailModal.corporate_detail.invoice_show_care_of && detailModal.corporate_detail.care_of && <div><b>Corp. C/O:</b> {detailModal.corporate_detail.care_of}</div>}
                  {detailModal.corporate_detail.invoice_show_gst !== false && detailModal.corporate_detail.gst_number && <div><b>Corp. GSTIN:</b> {detailModal.corporate_detail.gst_number}</div>}
                  {detailModal.corporate_detail.invoice_show_address !== false && detailModal.corporate_detail.billing_address && <div style={{ gridColumn: '1 / -1' }}><b>Corp. Address:</b> {detailModal.corporate_detail.billing_address}</div>}
                  {detailModal.corporate_detail.invoice_show_employee_id && detailModal.guest_detail?.employee_id && <div><b>Employee ID:</b> {detailModal.guest_detail.employee_id}</div>}
                  {detailModal.corporate_detail.invoice_show_vessel_name && detailModal.guest_detail?.vessel_name && <div><b>Vessel Name(s):</b> {detailModal.guest_detail.vessel_name}</div>}
                </>
              )}
              <div><b>Room:</b> {(Array.isArray(detailModal.room_numbers) && detailModal.room_numbers.length ? detailModal.room_numbers.join(', ') : detailModal.room_number) || '-'} {detailModal.room_type_name ? `(${detailModal.room_type_name})` : ''}</div>
              <div><b>Settlement:</b> {detailModal.settlement_mode || '-'}</div>
              <div><b>Check-In:</b> {detailModal.checkin_datetime ? String(detailModal.checkin_datetime).slice(0,16).replace('T',' ') : '-'}</div>
              <div><b>Check-Out:</b> {detailModal.checkout_datetime ? String(detailModal.checkout_datetime).slice(0,16).replace('T',' ') : '-'}</div>
            </div>

            {detailModal.payment_collected_mode && (
              <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '7px', padding: '0.55rem 0.7rem', marginBottom: '0.7rem', fontSize: '0.85rem', color: '#065f46' }}>
                <b>Payment Actually Collected:</b> {detailModal.payment_collected_mode} — {toMoney(detailModal.payment_collected_amount)}
                {detailModal.payment_collected_at ? ` on ${String(detailModal.payment_collected_at).slice(0, 10)}` : ''}
                {detailModal.payment_collected_reference ? ` (Ref: ${detailModal.payment_collected_reference})` : ''}
                {detailModal.payment_collected_remarks ? <div style={{ marginTop: 2, color: '#047857' }}>{detailModal.payment_collected_remarks}</div> : null}
              </div>
            )}

            {hasSplits && (
              <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <button onClick={() => setSplitTab(0)} style={tabBtnStyle(splitTab === 0)}>All Lines</button>
                {splitNos.map(sn => (
                  <button key={sn} onClick={() => setSplitTab(sn)} style={tabBtnStyle(splitTab === sn)}>Split {sn}</button>
                ))}
              </div>
            )}

            <div style={{ overflowX: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#ebf8ff' }}>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Posting</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Rate</th>
                    <th style={{ padding: '0.35rem', textAlign: 'center' }}>Tax%</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>SGST</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>CGST</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Debit</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Credit</th>
                    {canShowAmend && <th style={{ padding: '0.35rem', textAlign: 'center' }}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {activeLines.map((line, i) => {
                    const baseAmt = Number(line.debit_amount || 0) - Number(line.tax_amount || 0);
                    const isPaymentLine = PAYMENT_TYPES.has(line.posting_type);
                    const isVoidable = !isPaymentLine && Number(line.debit_amount || 0) > 0 && !line.is_reversed;
                    return (
                      <tr key={line.ledger_id || i} style={{ borderTop: '1px solid #edf2f7' }}>
                        <td style={{ padding: '0.35rem' }}>{i + 1}</td>
                        <td style={{ padding: '0.35rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{line.posting_date || '-'}</td>
                        <td style={{ padding: '0.35rem' }}>{line.posting_type || '-'}</td>
                        <td style={{ padding: '0.35rem', fontSize: '0.78rem' }}>{line.revenue_desc || line.remarks || '-'}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.debit_amount || 0) > 0 ? toMoney(baseAmt) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'center' }}>{line.gst_percent ? Number(line.gst_percent) + '%' : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.sgst_amount || 0) > 0 ? toMoney(line.sgst_amount) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.cgst_amount || 0) > 0 ? toMoney(line.cgst_amount) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(line.debit_amount)}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(line.credit_amount)}</td>
                        {canShowAmend && (
                          <td style={{ padding: '0.35rem', textAlign: 'center' }}>
                            {isVoidable ? (
                              <button
                                onClick={() => voidLedgerLine(line)}
                                disabled={voidingLine === line.ledger_id}
                                title="Void this charge line (audit-logged, admin only)"
                                style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', opacity: voidingLine === line.ledger_id ? 0.6 : 1 }}
                              >
                                {voidingLine === line.ledger_id ? '…' : 'Void'}
                              </button>
                            ) : (
                              <span style={{ fontSize: '0.7rem', color: '#a0aec0' }} title="Payment lines cannot be voided">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!activeLines.length && <tr><td colSpan={canShowAmend ? 11 : 10} style={{ padding: '0.55rem', color: '#718096' }}>No lines</td></tr>}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--theme-bg-light)', fontWeight: 700 }}>
                    <td colSpan={8} style={{ padding: '0.35rem' }}>Totals</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(activeDebit)}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(activeCredit)}</td>
                    {canShowAmend && <td />}
                  </tr>
                  <tr style={{ background: '#fff5f5' }}>
                    <td colSpan={8} style={{ padding: '0.35rem', fontWeight: 700, color: '#c53030' }}>Net Payable</td>
                    <td colSpan={canShowAmend ? 3 : 2} style={{ padding: '0.35rem', textAlign: 'right', fontWeight: 700, color: '#c53030' }}>{toMoney(activeDebit - activeRealCredit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ── Post Charge / Payment form (cancelled invoice, or any invoice with a balance) ── */}
            {canShowAmend && (String(detailModal.invoice_status || '').toUpperCase() === 'CANCELLED' || (activeDebit - activeCredit) > 0.005) && postChargeVisible && (() => {
              const isPay = postChargeForm.posting_type === 'PAYMENT';
              const base = Number(postChargeForm.base_amount) || 0;
              const gst  = isPay ? 0 : (Number(postChargeForm.gst_percent) || 0);
              const tax  = Math.round(base * gst / 100 * 100) / 100;
              const total = Math.round((base + tax) * 100) / 100;
              return (
                <div style={{ marginTop: '0.8rem', background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: '8px', padding: '0.9rem' }}>
                  <div style={{ fontWeight: 700, color: '#744210', marginBottom: '0.6rem', fontSize: '0.9rem' }}>Post New Charge</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                      Posting Type
                      <select value={postChargeForm.posting_type}
                        onChange={e => {
                          const t = e.target.value;
                          const labels = { PAYMENT:'Payment received', ROOM_TARIFF:'Room Tariff', KOT_ROOM_CHARGE:'Food & Beverage', BAR_ROOM_CHARGE:'Bar Charges', ROOM_SERVICE:'Room Service', LAUNDRY:'Laundry', MINIBAR_ROOM_CHARGE:'Minibar', LATE_CHECKOUT:'Late Checkout', MANUAL:'Miscellaneous', BANQUET_CHARGE:'Banquet Charges', ALLOWANCE:'Allowance' };
                          setPostChargeForm(f => ({ ...f, posting_type: t, revenue_desc: labels[t] || f.revenue_desc }));
                        }}
                        style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }}>
                        <option value="PAYMENT">💰 Payment / Settlement (collect)</option>
                        <option value="ROOM_TARIFF">Room Tariff</option>
                        <option value="KOT_ROOM_CHARGE">Food & Beverage</option>
                        <option value="BAR_ROOM_CHARGE">Bar Charges</option>
                        <option value="ROOM_SERVICE">Room Service</option>
                        <option value="LAUNDRY">Laundry</option>
                        <option value="MINIBAR_ROOM_CHARGE">Minibar</option>
                        <option value="LATE_CHECKOUT">Late Checkout</option>
                        <option value="MANUAL">Miscellaneous</option>
                        <option value="BANQUET_CHARGE">Banquet Charges</option>
                      </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                      Description
                      <input value={postChargeForm.revenue_desc}
                        onChange={e => setPostChargeForm(f => ({ ...f, revenue_desc: e.target.value }))}
                        style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                      {isPay ? 'Amount Received (₹)' : 'Base Amount (₹)'}
                      <input type="number" min="0.01" step="0.01" value={postChargeForm.base_amount}
                        onChange={e => setPostChargeForm(f => ({ ...f, base_amount: e.target.value }))}
                        placeholder={isPay ? 'e.g. 4725' : 'e.g. 6000'}
                        style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }} />
                    </label>
                    {isPay ? (
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                        Payment Mode
                        <select value={postChargeForm.payment_mode}
                          onChange={e => setPostChargeForm(f => ({ ...f, payment_mode: e.target.value }))}
                          style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }}>
                          <option value="CASH">Cash</option>
                          <option value="CARD">Card</option>
                          <option value="UPI">UPI</option>
                          <option value="BANK_TRANSFER">Bank Transfer</option>
                          <option value="CHEQUE">Cheque</option>
                        </select>
                      </label>
                    ) : (
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                        GST %
                        <select value={postChargeForm.gst_percent}
                          onChange={e => setPostChargeForm(f => ({ ...f, gst_percent: e.target.value }))}
                          style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }}>
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                        </select>
                      </label>
                    )}
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                      Posting Date
                      <input type="date" value={postChargeForm.posting_date}
                        onChange={e => setPostChargeForm(f => ({ ...f, posting_date: e.target.value }))}
                        style={{ padding: '0.35rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }} />
                    </label>
                  </div>
                  {base > 0 && (
                    <div style={{ fontSize: '0.82rem', color: isPay ? '#166534' : '#744210', background: isPay ? '#f0fdf4' : '#fef3c7', borderRadius: 5, padding: '0.4rem 0.6rem', marginBottom: '0.5rem', display: 'inline-block' }}>
                      {isPay
                        ? <>Recording payment of <b>₹{base.toFixed(2)}</b> ({postChargeForm.payment_mode}) · balance now ₹{Math.max(0, (activeDebit - activeCredit) - base).toFixed(2)}</>
                        : <>Base ₹{base.toFixed(2)} + GST ₹{tax.toFixed(2)} = <b>Total ₹{total.toFixed(2)}</b></>}
                    </div>
                  )}
                  {postChargeMsg && <div style={{ color: '#c53030', fontSize: '0.82rem', marginBottom: '0.4rem' }}>{postChargeMsg}</div>}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem' }}>
                    <button onClick={doPostCharge} disabled={postChargeLoading}
                      style={{ padding: '0.4rem 1rem', background: '#d97706', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 700, cursor: postChargeLoading ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}>
                      {postChargeLoading ? 'Posting...' : (isPay ? 'Record Payment' : 'Post Charge')}
                    </button>
                    <button onClick={() => { setPostChargeVisible(false); setPostChargeMsg(''); }}
                      style={{ padding: '0.4rem 0.8rem', background: '#fff', border: '1px solid #cbd5e0', borderRadius: 5, cursor: 'pointer', fontSize: '0.85rem' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.45rem', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              {canShowAmend && (String(detailModal.invoice_status || '').toUpperCase() === 'CANCELLED' || (activeDebit - activeCredit) > 0.005) && (
                <button onClick={() => {
                    const opening = !postChargeVisible;
                    setPostChargeVisible(v => !v); setPostChargeMsg('');
                    const bal = Math.round((activeDebit - activeCredit) * 100) / 100;
                    // Opening on an invoice that still owes money → default to recording the payment.
                    if (opening && bal > 0.005) {
                      setPostChargeForm(f => ({ ...f, posting_type: 'PAYMENT', revenue_desc: 'Payment received', base_amount: String(bal), payment_mode: f.payment_mode || 'CASH' }));
                    }
                  }}
                  style={{ padding: '0.3rem 0.65rem', borderRadius: '4px', border: '1px solid #d97706', background: postChargeVisible ? '#fffbeb' : '#fff', color: '#d97706', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                  + Post Charge / Payment
                </button>
              )}
              <span style={{ fontSize: '0.78rem', color: '#718096', marginRight: '0.2rem' }}>Print as:</span>
              {['Original', 'Duplicate', 'Triplicate'].map(copy => (
                <button key={copy} onClick={() => handlePrint(detailModal, activeLines, splitLabel, copy)}
                  style={{ padding: '0.3rem 0.65rem', borderRadius: '4px', border: '1px solid #c6f6d5', background: '#f0fff4', color: '#276749', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                  {copy}
                </button>
              ))}
              <button onClick={() => shareWhatsApp(detailModal)} style={{ padding: '0.3rem 0.65rem', borderRadius: '4px', border: '1px solid #c6f6d5', background: '#fff', color: '#25d366', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>📱 WhatsApp</button>
              <button onClick={() => shareEmail(detailModal)} disabled={emailSending === detailModal?.invoice_id} style={{ padding: '0.3rem 0.65rem', borderRadius: '4px', border: '1px solid var(--theme-border)', background: '#fff', color: '#4a5568', cursor: emailSending === detailModal?.invoice_id ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>{emailSending === detailModal?.invoice_id ? '⏳ Sending…' : '✉ Email'}</button>
              <button onClick={() => { setRefundModal(detailModal); setRefundForm({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' }); }} style={{ padding: '0.3rem 0.65rem', borderRadius: '4px', border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>↩ Refund</button>
              <button onClick={() => setDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Refund Modal ── */}
      {refundModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Record Refund — Invoice #{refundModal.invoice_number || refundModal.invoice_id}</h3>
            <div style={{ marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.9rem' }}>
              Guest: <b>{refundModal.guest_name || refundModal.guest_detail?.guest_name || '—'}</b>
            </div>
            <label style={{ display: 'block', marginBottom: '0.6rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Refund Amount (₹)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={refundForm.refund_amount}
                onChange={e => setRefundForm(f => ({ ...f, refund_amount: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '1rem', boxSizing: 'border-box' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: '0.6rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Refund Mode</span>
              <select
                value={refundForm.refund_mode}
                onChange={e => setRefundForm(f => ({ ...f, refund_mode: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '1rem' }}
              >
                {['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'OTHER'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Reason</span>
              <input
                type="text"
                value={refundForm.refund_reason}
                onChange={e => setRefundForm(f => ({ ...f, refund_reason: e.target.value }))}
                placeholder="e.g. Guest complaint, billing error"
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '1rem', boxSizing: 'border-box' }}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setRefundModal(null)} disabled={refundLoading} style={{ padding: '0.5rem 1rem', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={doRefund} disabled={refundLoading} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {refundLoading ? 'Recording...' : 'Record Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
